import React, { useState, useRef, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Stepper, Step, StepLabel, Button, IconButton } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

import { parseCompanyDataFile } from '../../utils/companyDataParser';
import useCompanyDataUpload from '../../hooks/useCompanyDataUpload';
import { formatFinancialsForAPI, formatBasicInfoForAPI } from '../../utils/companyDataFormatters';
import { ErrorMessageContext } from '../../contexts';

import './CompanyDataUploadModal.scss';

export default function CompanyDataUploadModal({ 
  open, 
  onClose, 
  userData, 
  setUserData,
  onSuccess 
}) {
  const [file, setFile] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [fileIsDraggingOver, setFileIsDraggingOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [parseErrors, setParseErrors] = useState([]);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [uploadResults, setUploadResults] = useState(null);
  
  const uploadEl = useRef(null);
  const dragAndDropAreaRef = useRef(null);
  
  const { uploadCompanyData, uploading } = useCompanyDataUpload();
  const { setShowErrorMessage } = useContext(ErrorMessageContext);
  
  const validFileTypes = ['.csv', '.xls', '.xlsx', '.txt'];
  
  const uploadSteps = [
    'Parse & Validate',
    'General Info',
    'Cap Structure',
    'Holdings',
    'Financials & 820 inputs',
    'Industry & Comps'
  ];
  
  // Reset modal state
  const resetState = () => {
    setFile(null);
    setFileUploading(false);
    setUploadComplete(false);
    setProgress(0);
    setCurrentStep(0);
    setParsedData(null);
    setParseErrors([]);
    setParseWarnings([]);
    setUploadResults(null);
  };
  
  const handleClose = () => {
    if (!fileUploading) {
      resetState();
      onClose();
    }
  };
  

  const handleFileUpload = async (event, filesToUpload) => {
    event.preventDefault();
    const uploadedFile = filesToUpload[0];
    
    if (!uploadedFile) return;
    
    // Validate file size
    if (uploadedFile.size >= 35000000) {
      setShowErrorMessage('File size is too big (max 35MB)');
      return;
    }
    
    // Validate file type
    if (!validFileTypes.some(fileType => uploadedFile.name.toLowerCase().endsWith(fileType))) {
      setShowErrorMessage('Please upload a valid file (.csv, .xls, .xlsx, or .txt)');
      return;
    }
    
    setFile(uploadedFile);
    setFileUploading(true);
    setShowErrorMessage('');
    
    try {
      // Parse the file
      const { data, validation } = await parseCompanyDataFile(uploadedFile);
      
      console.log('File parsed successfully:', data);
      
      setParsedData(data);
      setParseErrors(validation.errors);
      setParseWarnings(validation.warnings);
      
      if (!validation.isValid) {
        setFileUploading(false);
        setShowErrorMessage('File validation failed. Please check errors below.');
        return;
      }
      
      // Create updated userData with financials BEFORE calling uploadCompanyData
      const financialData = formatFinancialsForAPI(data.financials);
      const basicInfo = formatBasicInfoForAPI(data.basicInputs);
      
      const updatedUserData = {
        ...userData,
        transactionData: {
          ...userData.transactionData,
          basicInfo: {
            ...userData.transactionData.basicInfo,
            ...basicInfo,
            ...(financialData.debtAsOfTransactionDate !== undefined && {
              debtAsOfTransactionDate: financialData.debtAsOfTransactionDate
            })
          },
          clientFinancialInfo: {
            ...userData.transactionData.clientFinancialInfo,
            ...financialData.clientFinancialInfo
          },
          enterpriseFinancialInfo: {
            ...userData.transactionData.enterpriseFinancialInfo,
            ...financialData.enterpriseFinancialInfo
          }
        }
      };
      
      console.log('Updated userData for API:', JSON.stringify({
        clientFinancialInfo: updatedUserData.transactionData.clientFinancialInfo,
        enterpriseFinancialInfo: updatedUserData.transactionData.enterpriseFinancialInfo
      }, null, 2));
      
      // Update local state for UI
      setUserData(updatedUserData);
      
      // Start upload process with UPDATED userData
      setProgress(0);
      setCurrentStep(0);
      
      const result = await uploadCompanyData(
        data,
        updatedUserData, // Use updated userData instead of old one
        (step, progressValue) => {
          setCurrentStep(step);
          setProgress(progressValue);
        }
      );
      
      setUploadResults(result.results);
      setUploadComplete(true);
      setFileUploading(false);
      
      // If successful, trigger refresh after a delay
      if (result.success) {
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 2000);
      }
      
    } catch (error) {
      setFileUploading(false);
      setShowErrorMessage(error.message || 'Failed to process file');
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFileIsDraggingOver(true);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFileIsDraggingOver(false);
    handleFileUpload(e, e.dataTransfer.files);
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFileIsDraggingOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFileIsDraggingOver(false);
  };
  
  const dragAndDropArea = useCallback((node) => {
    if (dragAndDropAreaRef.current) {
      dragAndDropAreaRef.current.removeEventListener('dragover', handleDragOver);
      dragAndDropAreaRef.current.removeEventListener('drop', handleDrop);
      dragAndDropAreaRef.current.removeEventListener('dragenter', handleDragEnter);
      dragAndDropAreaRef.current.removeEventListener('dragleave', handleDragLeave);
    }
    if (node) {
      node.addEventListener('dragover', handleDragOver, { passive: false });
      node.addEventListener('drop', handleDrop, { passive: false });
      node.addEventListener('dragenter', handleDragEnter, { passive: false });
      node.addEventListener('dragleave', handleDragLeave, { passive: false });
    }
    dragAndDropAreaRef.current = node;
  }, []);
  
  const renderUploadArea = () => {
    if (uploadComplete) {
      return (
        <div className="upload-results">
          <h3>Upload Complete!</h3>
            {uploadResults && (
            <div className="results-summary">
              {Object.entries(uploadResults).map(([section, result]) => (
                <div key={`result-${section}`} className="result-item">
                  {result.success ? (
                    <CheckCircleIcon className="success-icon" />
                  ) : result.skipped ? (
                    <WarningIcon className="warning-icon" />
                  ) : (
                    <ErrorIcon className="error-icon" />
                  )}
                  <div className="result-content">
                    <strong>{section.charAt(0).toUpperCase() + section.slice(1).replace(/([A-Z])/g, ' $1')}</strong>
                    {result.success && <span className="result-text">{result.data}</span>}
                    {result.skipped && <span className="result-text">{result.data}</span>}
                    {!result.success && !result.skipped && (
                      <span className="error-text">{result.error}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="result-footer mb-3">
            <p>Review the results and manually correct any failed sections if needed.</p>
            <Button
              variant="contained"
              onClick={handleClose}
              className="close-btn"
            >
              Close
            </Button>
          </div>
        </div>
      );
    }
    
    if (fileUploading) {
      return (
        <div className="upload-progress">
          <h3>{uploadSteps[currentStep]}</h3>
          <div className="progress-section">
            <div className="progress-header">
              <span>Progress</span>
              <span className="progress-percent">{Math.round(progress)}%</span>
            </div>
            <LinearProgress variant="determinate" value={progress} />
          </div>
          <Stepper activeStep={currentStep} orientation="vertical" className="upload-stepper">
            {uploadSteps.map((label, index) => (
              <Step key={label} completed={index < currentStep}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
      );
    }
    
    return (
      <div className="file-upload-area">
        <div
          className={`drag-and-drop-area${fileIsDraggingOver ? ' file-dragging' : ''}`}
          ref={dragAndDropArea}
        >
          <UploadFileIcon className="upload-icon" />
          <h4>Upload Company Data</h4>
          <p>Drop your tab-delimited file here</p>
          <span>or</span>
          <Button
            variant="contained"
            onClick={() => uploadEl.current.click()}
            className="browse-btn"
          >
            Browse Files
          </Button>
          <p className="file-types">Supported: .csv, .xls, .xlsx, .txt (max 35MB)</p>
          <input
            ref={uploadEl}
            type="file"
            className="file-input"
            accept={validFileTypes.join(',')}
            onChange={(e) => handleFileUpload(e, e.target.files)}
          />
        </div>
        
        {(parseErrors.length > 0 || parseWarnings.length > 0) && (
          <div className="validation-messages">
            {parseErrors.length > 0 && (
              <div className="errors">
                <h4>
                  <ErrorIcon /> Errors
                </h4>
                <ul>
                  {parseErrors.map((error) => (
                    <li key={`error-${error}`}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            {parseWarnings.length > 0 && (
              <div className="warnings">
                <h4>
                  <WarningIcon /> Warnings
                </h4>
                <ul>
                  {parseWarnings.map((warning) => (
                    <li key={`warning-${warning}`}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className="company-data-upload-modal"
      disableScrollLock
    >
      <div className="modal-header">
        <h2>Upload Company Data</h2>
        <IconButton onClick={handleClose} disabled={fileUploading}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className="modal-content">
        {renderUploadArea()}
      </div>
    </Dialog>
  );
}

CompanyDataUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userData: PropTypes.object.isRequired,
  setUserData: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

CompanyDataUploadModal.defaultProps = {
  onSuccess: null
};